import { useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';
import { useIsPartner } from '../../features/referral/hooks/usePartnerProgram.ts';
import { getTournamentNavPath } from '../../features/tournament/lib/tournament-nav.ts';
import useAppStore from '../../store/rootStore.ts';
import { NAV_ITEMS, type NavItem, PARTNER_NAV_LABEL, REFERRALS_NAV_VALUE } from './constants';

/** NavItem with its label descriptor resolved to the active locale. */
export interface DisplayNavItem extends Omit<NavItem, 'label'> {
  label: string;
}

export function useFilteredNavItems(): DisplayNavItem[] {
  const { t } = useLingui();
  const pointsEnabled = useAppStore((state) => state.config?.features.pointsEnabled);
  const rewardsEnabled = useAppStore((state) => state.config?.features.rewardsEnabled);
  const referralDashboardEnabled = useAppStore((state) => state.config?.features.referralDashboardEnabled);
  const tournamentEnabled = useAppStore((state) => state.config?.features.tournamentEnabled);
  const apiKeysEnabled = useAppStore((state) => state.config?.features.apiKeysEnabled);
  const changelogEnabled = useAppStore((state) => state.config?.features.changelogEnabled);
  const fundingRatesEnabled = useAppStore((state) => state.config?.features.fundingRatesEnabled);
  const dashboardEnabled = useAppStore((state) => !!state.config?.features.duneDashboardUrl);
  const showBuildersFundingHistory = useAppStore((state) => state.config?.features.showBuildersFundingHistory);
  const competitions = useAppStore((state) => state.config?.competitions);
  const hasCompetitions = (competitions?.length ?? 0) > 0;
  const tournamentNavPath = useMemo(() => getTournamentNavPath(competitions), [competitions]);
  // Shares the `profile-referrals` query with the referrals page.
  const isPartner = useIsPartner();

  return useMemo(
    () =>
      NAV_ITEMS.filter((item) => item.enabled)
        .filter((item) => (item.value === 5 ? pointsEnabled : true))
        .filter((item) => (item.value === REFERRALS_NAV_VALUE ? referralDashboardEnabled : true))
        .filter((item) => (item.value === 8 ? rewardsEnabled : true))
        .filter((item) => (item.value === 9 ? tournamentEnabled && hasCompetitions : true))
        .filter((item) => (item.value === 10 ? apiKeysEnabled : true))
        .filter((item) => (item.value === 14 ? changelogEnabled : true))
        .filter((item) => (item.value === 15 ? fundingRatesEnabled : true))
        .filter((item) => (item.value === 16 ? dashboardEnabled : true))
        .filter((item) => (item.value === 12 ? showBuildersFundingHistory : true))
        .map((item) => {
          const label = t(item.value === REFERRALS_NAV_VALUE && isPartner ? PARTNER_NAV_LABEL : item.label);
          if (item.value === 9) return { ...item, label, path: tournamentNavPath };
          return { ...item, label };
        }),
    [
      pointsEnabled,
      rewardsEnabled,
      referralDashboardEnabled,
      tournamentEnabled,
      hasCompetitions,
      apiKeysEnabled,
      changelogEnabled,
      fundingRatesEnabled,
      dashboardEnabled,
      showBuildersFundingHistory,
      tournamentNavPath,
      isPartner,
      t,
    ]
  );
}
